<template>
  <div v-if="loading" class="page-loading">
    <Loading />
  </div>
  <div
    v-if="isBack && isBackVisible"
    @click="handleBack"
    class="icon-button-container"
  >
    <span class="material-symbols-outlined">arrow_back</span> Back
  </div>
  <div v-if="!isDeveloperInfoVisible">
    <div>
      <div class="strip fs-14 between-flex">
        <div class="w-25">
          <SearchAndFilter
            v-model="searchQuery"
            :nullable="true"
            :showInput="true"
            :showSelect="false"
            :placeholder="$t('List.searchByListName')"
            @submitForm="handleSearchFilterChange"
          />
        </div>
        <div class="right-strip">
          <div class="filter-container">
            <FilterComponent
              :sortCriteria="sortCriteria"
              :sortOrder="sortOrder"
              :sortingOptions="sortingOptions"
              :orderOptions="orderOptions"
              :isOrderOptions="true"
              @filter-change="handleFilterChange"
            />
            <MultiSelectFilterComponent
              :available-filters="platformFilters"
              :initial-selected="selectedFilters"
              :placeholder-text="$t('Custom.allPlatforms')"
              @update:selected-filters="handlePlatformFilterChange"
            />
          </div>
          <a href="#" @click.prevent="openModal('create')">
            <span class="material-symbols-outlined"> playlist_add </span>
            {{ $t("new") }}
          </a>
        </div>
      </div>
      <NotFoundContent
        v-if="!userDeveloperList.length"
        :message="$t('List.listIsEmpty')"
      />
      <NotFoundContent
        v-else-if="filteredAndSortedItems.length === 0"
        :message="$t('Custom.notFoundResult')"
      />
      <div v-else class="list-items-content">
        <TableComponent>
          <template v-slot:table-header>
            <tr>
              <th v-for="header in tableHeaders" :key="header.key">
                {{ header.label }}
              </th>
            </tr>
          </template>
          <template v-slot:table-body>
            <tr
              v-for="(data, index) in filteredAndSortedItems"
              :key="data.list.id"
              :class="{ 'zebra-stripe': index % 2 === 0 }"
            >
              <td>{{ index + 1 }}</td>
              <td>
                <template
                  v-if="getStoreImage(data?.list?.platformTypeId).length"
                >
                  <div
                    class="action-buttons"
                    style="justify-content: flex-start"
                  >
                    <img
                      v-for="(src, indx) in getStoreImage(
                        data?.list?.platformTypeId
                      )"
                      :key="indx"
                      :src="src"
                      alt="Platform Icon"
                      class="platform-icon"
                    />
                  </div>
                </template>
              </td>
              <td>
                <a
                  class="clickable-text"
                  href="javascript:void(0)"
                  @click.stop="
                    showItems(
                      data.list.id,
                      data.sharingPermissionId,
                      data.list.name
                    )
                  "
                  >{{ data.list.name || "N/A" }}</a
                >
              </td>
              <td>{{ data.list.appCount || 0 }}</td>
              <td>
                <div
                  :class="[
                    'action-state',
                    permissionClass(data.sharingPermissionId),
                  ]"
                >
                  <span
                    v-if="!data.isOwner"
                    class="material-symbols-outlined fs-15 mr-5 icon"
                    >share</span
                  >
                  {{ permissionText(data.sharingPermissionId) }}
                </div>
              </td>
              <td>{{ formatDate(data.list.created_at) || "N/A" }}</td>
              <td>
                <div class="action-buttons">
                  <div
                    class="action-button"
                    :class="{
                      green: data?.isNotification,
                      red: !data?.isNotification,
                    }"
                    style="min-width: 84px"
                    :title="$t('Notification.name')"
                    @click.stop="
                      handleNotification(
                        data.listId,
                        data.userId,
                        data.isNotification,
                        index
                      )
                    "
                  >
                    <template v-if="loadingIndex === index">
                      <span class="button-spinner"></span>
                    </template>
                    <template v-else>
                      <span class="material-symbols-outlined notification-icon">
                        {{
                          data.isNotification
                            ? "notifications_active"
                            : "notification_add"
                        }}
                      </span>
                      <span>{{
                        data.isNotification ? "Is On" : "Is Off"
                      }}</span>
                    </template>
                  </div>
                  <div
                    v-if="permissionAccess(data.sharingPermissionId)"
                    class="action-button"
                    @click.stop="shareList(data.list.id)"
                  >
                    <span
                      class="material-symbols-outlined share-icon"
                      :title="$t('Custom.share')"
                      >share</span
                    >
                  </div>
                  <div
                    v-if="permissionAccess(data.sharingPermissionId)"
                    class="action-button"
                    @click.stop="openModal('update', data)"
                  >
                    <span
                      class="material-symbols-outlined edit-icon"
                      :title="$t('Custom.edit')"
                      >edit_square</span
                    >
                  </div>
                  <div
                    v-if="permissionAccess(data.sharingPermissionId)"
                    class="action-button"
                    @click.stop="handleAreYouSureListModal(data.list)"
                  >
                    <span
                      class="material-symbols-outlined remove-icon"
                      :title="$t('Custom.delete')"
                      >delete_sweep</span
                    >
                  </div>
                </div>
              </td>
            </tr>
          </template>
        </TableComponent>
        <div class="pagination-wrapper">
          <PaginationComponent
            :meta="pagination"
            @page-changed="fetchDeveloeprList"
          />
        </div>
      </div>
    </div>
  </div>

  <Modal
    :isOpen="isModalOpen"
    :modalTitle="modalTitle"
    :actionType="actionType"
    @close="closeModal"
    @action="handleModalAction"
  >
    <TextInput label="List Name" v-model="listName" :required="true" />
    <SelectInput
      label="Platform Type"
      v-model="selectedOption"
      :options="platformTypes"
      optionName="platformTypeName"
      placeholder="Select a platform"
      :isDisabled="actionType === 'update'"
      :required="true"
    />
  </Modal>

  <Modal
    :isOpen="isItemsModalOpen"
    :modalTitle="modalTitle"
    :actionButton="false"
    @close="closeModal"
  >
    <div>
      <NotFoundContent
        v-if="listItems.length === 0"
        :message="$t('List.listIsEmpty')"
      />
      <div v-else>
        <div class="mb-10">
          <SearchAndFilter
            v-model="searchItemQuery"
            v-model:modelValueSort="sortOrder"
            :showInput="true"
            :showSelect="false"
            :placeholder="$t('Apps.searchByAppName')"
          />
        </div>
        <NotFoundContent
          v-if="filteredAndSortedListItems.length === 0"
          message="Not found result."
        />
        <div class="list-items">
          <ItemCard
            v-for="data in filteredAndSortedListItems"
            :key="data.id"
            :data="data"
            :item="data.item"
            :menuId="developerMenuId"
            :isNotification="false"
            :isOptionMenu="true"
            @setItemMenu="setOpenDeveloperMenu"
            @toggleView="handleShow"
            :buttons="generateButtons(data)"
          />
        </div>
      </div>
    </div>
  </Modal>

  <Modal
    :isOpen="ShareModal"
    :modalTitle="modalTitle"
    :actionButton="false"
    @close="closeModal"
  >
    <div class="invite-section">
      <div class="input-select-container">
        <input
          type="email"
          v-model="inviteEmail"
          placeholder="Enter email"
          class="invite-input"
        />
        <select v-model="inviteRole" class="role-select">
          <option
            v-for="option in roleOptions"
            :key="option.id"
            :value="option.id"
          >
            {{ option.name }}
          </option>
        </select>
      </div>
      <button class="invite-btn" @click="sendInvite" :disabled="isLoading">
        <span v-if="!isLoading">Invite</span>
        <span v-else>
          <i class="spinner"></i>
        </span>
      </button>
    </div>

    <div class="user-list">
      <span>People with access</span>
      <ul>
        <li
          v-for="data in listUsersAccess"
          :key="data.user.id"
          class="user-item"
        >
          <div class="user-info">
            <img
              src="@/assets/img/avatar/avatar3.png"
              alt="Avatar"
              class="user-avatar"
            />
            <div class="user-details">
              <span class="user-name">
                {{ data.user.name }}
                <span v-if="isCurrentUser(data.user.id)" class="current-user"
                  >(Me)</span
                >
              </span>
              <span class="user-email">{{ data.user.email }}</span>
            </div>
          </div>
          <div class="user-actions">
            <div class="user-role">
              <div
                :class="[
                  'btn-state',
                  data.isOwner
                    ? 'bg-frozen'
                    : getPermissionClass(data.sharingPermissionId),
                ]"
              >
                {{
                  data.isOwner
                    ? "Owner"
                    : getPermissionText(data.sharingPermissionId)
                }}
              </div>
            </div>
            <div
              v-if="!data.isOwner"
              class="user-remove"
              @click="removeUserAccess(data)"
            >
              <span class="material-symbols-outlined remove-icon">remove</span>
            </div>
          </div>
        </li>
      </ul>
    </div>
  </Modal>

  <DeveloperInfo
    v-if="isDeveloperInfoVisible"
    :developerData="developerData"
    :isBack="true"
    @close="handleBack"
  />

  <AreYouSureModal
    v-if="showListModal"
    :isVisible="showListModal"
    :title="modalListTitle"
    :message="modalListMessage"
    :itemId="selectedListId"
    :confirmButtonLabel="$t('Custom.delete')"
    :cancelButtonLabel="$t('Custom.cancel')"
    @result="handleModalListResult"
  />

  <AreYouSureModal
    v-if="showModal"
    :isVisible="showModal"
    :title="modalTitle"
    :message="modalMessage"
    :itemId="selectedDeveloperId"
    confirmButtonLabel="Remove"
    cancelButtonLabel="Cancel"
    @result="handleModalResult"
  />

  <NotificationComponent
    v-if="notification.show"
    :key="notification.key"
    :type="notification.type"
    :title="notification.title"
    :message="notification.message"
    @closed="!notification.show"
  />
</template>

<script>
import Loading from "@/components/user/layout/LoadingComponent.vue";
import SharingPermissionEnum from "@/enums/SharingPermissionEnum";
import ListTypeEnum from "@/enums/ListTypeEnum";
import NotificationComponent from "@/components/custom/notifications/NotificationComponent.vue";
import PlatformTypeEnum from "@/enums/PlatformTypeEnum";
import NotFoundContent from "@/components/user/common/NotFoundContent.vue";
import ListCard from "@/components/user/common/ListCard.vue";
import SearchAndFilter from "@/components/user/common/SearchAndFilter.vue";
import Modal from "@/components/custom/modals/ModalComponent.vue";
import TextInput from "@/components/custom/inputs/TextInput.vue";
import SelectInput from "@/components/custom/inputs/SelectInput.vue";
import ItemCard from "@/components/user/common/ItemCard.vue";
import ButtonComponent from "@/components/custom/buttons/ButtonComponent.vue";
import AreYouSureModal from "@/components/custom/modals/AreYouSureModal.vue";
import DeveloperInfo from "@/components/user/viewComponent/developer/DeveloperInfo.vue";
import FilterComponent from "@/components/user/common/FilterComponent.vue";
import MultiSelectFilterComponent from "@/components/user/common/MultiSelectFilterComponent.vue";
import {
  getAllList,
  getListDevelopers,
  removeDeveloperFromList,
} from "@/api/developerList.api";
import {
  addListAccessToUser,
  createList,
  deleteList,
  getListUsers,
  removeListAccessFromUser,
  updateList,
  updateNotification,
} from "@/api/list.api";
import { getDeveloper, getDeveloperWithApps } from "@/api/developer.api";
import { getUserByEmail } from "@/api/user.api";
import TableComponent from "@/components/custom/table/TableComponent.vue";
import { storeImage } from "@/ultis/user/functions/storePlatform";
import PaginationComponent from "@/components/user/common/PaginationComponent.vue";

export default {
  name: "DeveloperListComponent",
  components: {
    Loading,
    NotFoundContent,
    ListCard,
    SearchAndFilter,
    Modal,
    TextInput,
    SelectInput,
    ItemCard,
    ButtonComponent,
    AreYouSureModal,
    NotificationComponent,
    DeveloperInfo,
    FilterComponent,
    MultiSelectFilterComponent,
    TableComponent,
    PaginationComponent,
  },
  data() {
    return {
      loading: false,
      notification: {
        show: false,
        key: 0,
        type: "",
        title: "",
        message: "",
      },
      pagination: {
        current_page: 1,
        last_page: 1,
        next_page_url: null,
        prev_page_url: null,
      },
      sortOrder: "asc",
      userDeveloperList: [],
      roleOptions: [],
      listItems: [],
      listUsersAccess: [],
      platformTypes: [],
      selectedOption: "",
      listName: "",
      modalTitle: "",
      actionType: "",
      inviteEmail: "",
      searchItemQuery: "",
      searchQuery: "",
      currentListId: null,
      developerData: null,
      selectedListId: null,
      sharingPermissionId: null,
      menuId: null,
      developerMenuId: null,
      selectedDeveloperId: null,
      inviteRole: null,
      showModal: false,
      isItemsModalOpen: false,
      isDeveloperInfoVisible: false,
      isModalOpen: false,
      ShareModal: false,
      isLoading: false,
      sortCriteria: "created_at",
      sortOrder: "desc",
      searchQuery: "",
      sortingOptions: [
        { value: "name", label: this.$t("Filter.name") },
        { value: "isOwner", label: this.$t("Filter.owner") },
        { value: "appCount", label: this.$t("Filter.developerCount") },
        { value: "created_at", label: this.$t("Filter.createdAt") },
      ],
      orderOptions: [
        { value: "asc", label: this.$t("Filter.ascending") },
        { value: "desc", label: this.$t("Filter.descending") },
      ],
      showFilterDropdown: false,
      selectedFilters: [],
      platformFilters: PlatformTypeEnum.getAllOptions(),
      tableHeaders: [
        { key: "index", label: this.$t("Table.index") },
        { key: "platform", label: this.$t("Table.platform") },
        { key: "name", label: this.$t("Table.name") },
        { key: "appCount", label: this.$t("Table.appCount") },
        { key: "permission", label: this.$t("Table.permission") },
        { key: "createdAt", label: this.$t("Table.createdAt") },
      ],
      modalListTitle: "",
      modalListMessage: "",
      modalMessage: "",
      showListModal: false,
      loadingIndex: null,
    };
  },
  computed: {
    filteredAndSortedItems() {
      let items = [...this.userDeveloperList];
      return items;

      if (this.searchQuery) {
        items = items.filter((item) =>
          item.list.name
            .toLowerCase()
            .includes(this.searchQuery.toLowerCase())
        );
      }

      if (this.selectedFilters.length > 0) {
        items = items.filter((item) =>
          this.selectedFilters.includes(item.list.platformTypeId)
        );
      }

      return items.sort((a, b) => {
        const modifier = this.sortOrder === "asc" ? 1 : -1;

        switch (this.sortCriteria) {
          case "name":
            return a.list.name.localeCompare(b.list.name) * modifier;

          case "isOwner":
            return (a.isOwner - b.isOwner) * modifier;

          case "appCount":
            return (a.list.appCount - b.list.appCount) * modifier;
          default:
            return 0;
        }
      });
    },
    filteredAndSortedListItems() {
      let filteredItems = this.listItems.filter((item) =>
        item.item.name
          .toLowerCase()
          .includes(this.searchItemQuery.toLowerCase())
      );
      return filteredItems.sort((a, b) => {
        let modifier = this.sortOrder === "asc" ? 1 : -1;
        return a.item.name.localeCompare(b.item.name) * modifier;
      });
    },
    isBackVisible() {
      return this.isBack && this.isDeveloperInfoVisible;
    },
  },
  methods: {
    permissionClass(sharingPermissionId) {
      switch (sharingPermissionId) {
        case SharingPermissionEnum.ALL:
          return "bg-accepted";
        case SharingPermissionEnum.READ:
          return "bg-hold";
        case SharingPermissionEnum.EDIT:
          return "bg-edit";
        case SharingPermissionEnum.DELETE:
          return "bg-rejected";
        default:
          return "bg-rejected";
      }
    },
    permissionText(sharingPermissionId) {
      return SharingPermissionEnum.fromId(sharingPermissionId) || "Unknown";
    },
    getStoreImage(platformTypeId) {
      return storeImage(platformTypeId);
    },
    formatDate(date) {
      if (!date) return "";
      const locale = localStorage.getItem("localeTime") || "en-US";
      return new Date(date).toLocaleDateString(locale);
    },
    async handlePlatformFilterChange(filters) {
      this.selectedFilters = filters;
      this.loading = true;
      await this.fetchDeveloeprList();
    },
    async handleFilterChange({ criteria, order }) {
      this.sortCriteria = criteria;
      this.sortOrder = order;
      console.log("Criteria:", this.sortCriteria, "Order:", this.sortOrder);
      this.loading = true;
      await this.fetchDeveloeprList();
    },
    async handleSearchFilterChange() {
      this.loading = true;
      await this.fetchDeveloeprList();
    },
    async fetchDeveloeprList(page = 1) {
      try {
        const response = await getAllList(page, this.sortCriteria, this.sortOrder, this.selectedFilters, this.searchQuery);
        if (response.data.status) {
          this.userDeveloperList = response.data.data.data;
          this.pagination = response.data.data.meta;
        } else {
          console.error(response.data.msg);
        }
      } catch (error) {
        console.error("Error fetching developer list:", error);
      } finally {
        this.loading = false;
      }
    },
    async createList() {
      this.loading = true;
      try {
        const response = await createList(
          this.listName,
          this.selectedOption ? this.selectedOption : PlatformTypeEnum.ALL,
          ListTypeEnum.DEVELOPER,
          false
        );
        if (response.data.status) {
          this.showNotification(
            "success",
            this.$t("Custom.success"),
            response.data.msg
          );
          await this.fetchDeveloeprList();
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      }
      this.loading = false;
    },
    async updateList() {
      this.loading = true;
      try {
        const response = await updateList(this.currentListId, this.listName);

        if (response.data.status) {
          await this.fetchDeveloeprList();
          this.showNotification(
            "success",
            this.$t("Custom.success"),
            response.data.msg
          );
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      }
      this.loading = false;
    },
    async handleModalAction(actionType) {
      if (actionType === "create") {
        this.createList();
      } else {
        this.updateList();
      }
      this.closeModal();
    },
    async getListDeveloper(listId) {
      this.loading = true;
      try {
        const response = await getListDevelopers(listId);
        if (response.data.status) {
          this.listItems = response.data.data;
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      } finally {
        this.loading = false;
      }
    },
    async handleShow(storeId) {
      // this.loading = true;
      // try {
      //   const response = await getDeveloperWithApps(developerId);
      //   if (response.data.status) {
      //     this.developerData = response.data.data;
      //     this.closeModal();
      //     this.isDeveloperInfoVisible = true;
      //   } else {
      //     this.showNotification("hold", "Error", response.data.msg);
      //   }
      // } catch (error) {
      //   this.showNotification(
      //     "error",
      //     this.$t("Custom.error"),
      //     error.response.data.msg
      //   );
      // } finally {
      //   this.loading = false;
      // }
      this.$router.push({
        name: "Developer",
        params: { storeId: storeId },
      });
    },
    async processDeveloperLink(url = this.url) {
      if (!url) return;
      try {
        const response = await getDeveloper(url);

        if (response.data.status) {
          this.developerData = response.data.data;
          this.showNotification("success", "Suceess", response.data.msg);
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      } finally {
      }
    },
    async removeDeveloper(developerId, listId) {
      this.loading = true;
      try {
        const response = await removeDeveloperFromList(developerId, listId);
        if (response.data.status) {
          await this.fetchDeveloeprList();
          this.showNotification("success", "Suceess", response.data.msg);
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      } finally {
        this.loading = false;
      }
    },
    async deleteList(listId) {
      this.loading = true;
      try {
        const response = await deleteList(listId);
        if (response.data.status) {
          await this.fetchDeveloeprList();
          this.showNotification("success", "Suceess", response.data.msg);
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      } finally {
        this.loading = false;
      }
    },
    async fetchListUsers(listId) {
      this.loading = true;
      try {
        const response = await getListUsers(listId);
        if (response.data.status) {
          this.listUsersAccess = response.data.data;
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      } finally {
        this.loading = false;
      }
    },
    async getUserIdByEmail(email) {
      try {
        const response = await getUserByEmail(email);
        if (response.data.status) {
          return response.data.data.id;
        } else if (response.data.errNum === 404) {
          this.showNotification("hold", "Error", response.data.msg);
          return null;
        }
      } catch (error) {
        this.showNotification(
          "hold",
          "Error",
          error.response?.data?.msg || "Failed to fetch user ID."
        );
        return null;
      }
    },
    async addAccess(listId, userId, sharingPermissionId) {
      try {
        const response = await addListAccessToUser(
          listId,
          userId,
          sharingPermissionId,
          false
        );
        if (response.data.status) {
          await this.fetchListUsers(listId);
          this.showNotification("success", "Suceess", response.data.msg);
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      }
    },
    async handleNotification(listId, userId, isNotification, index) {
      this.loadingIndex = index;
      try {
        const response = await updateNotification(
          listId,
          userId,
          !isNotification
        );
        if (response.data.status) {
          await this.fetchDeveloeprList();
          this.showNotification("success", "Suceess", response.data.msg);
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      } finally {
        this.loadingIndex = null;
      }
    },
    async sendInvite() {
      this.isLoading = true;
      try {
        if (!this.isValidEmail(this.inviteEmail)) {
          this.showNotification("hold", "Error", "Please enter a valid email.");
          return;
        }
        if (!this.inviteEmail || !this.inviteRole) {
          this.showNotification("hold", "Error", "Fields cannot be empty.");
          return;
        }
        if (this.isEmailAlreadyInvited(this.inviteEmail)) {
          this.showNotification(
            "hold",
            "Error",
            "This email is already invited to the list."
          );
          return;
        }
        const userId = await this.getUserIdByEmail(this.inviteEmail);
        if (!userId) {
          return;
        }
        await this.addAccess(this.currentListId, userId, this.inviteRole);
        await this.fetchListUsers(this.currentListId);
        this.inviteEmail = "";
        this.inviteRole = this.roleOptions[0].id;
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      } finally {
        this.isLoading = false;
      }
    },
    async removeUserAccess(data) {
      this.loading = true;
      try {
        const response = await removeListAccessFromUser(
          data.listId,
          data.userId
        );
        if (response.data.status) {
          await this.fetchListUsers(data.listId);
          this.showNotification(
            "success",
            this.$t("Custom.success"),
            response.data.msg
          );
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      } finally {
        this.loading = false;
      }
    },
    async showItems(listId, sharingPermissionId, listName) {
      this.selectedListId = listId;
      await this.getListDeveloper(listId);
      this.modalTitle = listName;
      this.isItemsModalOpen = true;
      this.sharingPermissionId = sharingPermissionId;
    },
    shareList(listId) {
      this.modalTitle = "Share User List";
      this.currentListId = listId;
      this.fetchListUsers(listId);
      this.ShareModal = true;
    },
    openModal(type, item = null) {
      this.modalTitle = type === "create" ? "Create New List" : "Edit List";
      this.actionType = type;
      if (type === "update" && item) {
        this.currentListId = item.list.id;
        this.listName = item.list.name;
        this.selectedOption = item.list.platformTypeId;
      }
      this.isModalOpen = true;
    },
    closeModal() {
      this.isModalOpen = false;
      this.isItemsModalOpen = false;
      this.ShareModal = false;
      this.listItems = [];
      this.listName = "";
      this.selectedOption = "";
      this.currentListId = null;
      this.sharingPermissionId = null;
    },
    setOpenDeveloperMenu(menuId) {
      this.developerMenuId = this.developerMenuId === menuId ? null : menuId;
    },
    setMenuId(menuId) {
      this.menuId = this.menuId === menuId ? null : menuId;
    },
    generateButtons(data) {
      return [
        {
          label: this.$t("Custom.show"),
          icon: "visibility",
          disable: false,
          action: () => this.handleShow(data.item.storeId),
        },
        {
          label: this.$t("Custom.remove"),
          icon: "remove",
          disable: this.isDisabled("remove"),
          action: () => {
            this.handleAreYouSureModal(data.item),
              this.setOpenDeveloperMenu(data.item.id);
          },
        },
      ];
    },
    permissionAccess(sharingPermissionId) {
      const allowedPermissions = [
        SharingPermissionEnum.ALL,
        SharingPermissionEnum.EDIT,
      ];
      return allowedPermissions.includes(sharingPermissionId);
    },
    isDisabled(button) {
      const permission = this.sharingPermissionId;
      switch (button) {
        case "show":
          return ![
            SharingPermissionEnum.ALL,
            SharingPermissionEnum.EDIT,
          ].includes(permission);
        case "showItems":
          return ![
            SharingPermissionEnum.ALL,
            SharingPermissionEnum.READ,
            SharingPermissionEnum.EDIT,
          ].includes(permission);
        case "share":
          return ![
            SharingPermissionEnum.ALL,
            SharingPermissionEnum.EDIT,
          ].includes(permission);
        case "remove":
          return !(permission === SharingPermissionEnum.ALL);
        default:
          return true;
      }
    },
    handleAreYouSureListModal(item) {
      this.closeModal();
      this.modalListTitle = this.$t("Custom.areYouSure");
      this.modalListMessage =
        this.$t("Custom.doYouWantDeleteThis") +
        "<b> " +
        item.name +
        "</b> ? " +
        this.$t("Custom.thisActionCannotBeUndone");
      this.selectedListId = item.id;
      this.showListModal = true;
    },
    handleModalListResult(result) {
      this.showListModal = false;
      if (result) {
        this.deleteList(this.selectedListId);
      }
    },
    handleAreYouSureModal(item) {
      this.modalTitle = "Are you sure?";
      this.modalMessage =
        "Do you want to remove this <b>" +
        item.name +
        "</b> ? This action cannot be undone.";
      this.selectedDeveloperId = item.id;
      this.showModal = true;
    },
    handleModalResult(result, developerId) {
      this.showModal = false;
      if (result) {
        this.removeDeveloper(developerId, this.selectedListId);
      }
      this.selectedListId = null;
      this.selectedDeveloperId = null;
      this.isItemsModalOpen = false;
    },
    fetchPlatformTypes() {
      this.platformTypes = PlatformTypeEnum.getAllOptions(true);
    },
    showNotification(type, title, message) {
      this.notification = {
        show: true,
        key: Date.now(),
        type,
        title,
        message,
      };
    },
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },
    getPermissionClass(permissionId) {
      switch (permissionId) {
        case SharingPermissionEnum.ALL:
          return "bg-accepted";
        case SharingPermissionEnum.READ:
          return "bg-hold";
        case SharingPermissionEnum.EDIT:
          return "bg-edit";
        case SharingPermissionEnum.DELETE:
          return "bg-rejected";
        default:
          return "bg-rejected";
      }
    },
    getPermissionText(permissionId) {
      switch (permissionId) {
        case SharingPermissionEnum.ALL:
          return "All";
        case SharingPermissionEnum.READ:
          return "Read";
        case SharingPermissionEnum.EDIT:
          return "Edit";
        default:
          return "Unknown";
      }
    },
    isCurrentUser(userId) {
      return userId === this.currentUserId;
    },
    isEmailAlreadyInvited(email) {
      return this.listUsersAccess.some((data) => data.user.email === email);
    },
    handleBack() {
      this.isDeveloperInfoVisible = false;
    },
    toggleFilterDropdown() {
      this.showFilterDropdown = !this.showFilterDropdown;
    },
    handleClickOutside(event) {
      if (
        this.$refs.filterDropdown &&
        !this.$refs.filterDropdown.contains(event.target)
      ) {
        this.showFilterDropdown = false;
      }
    },
  },
  async created() {
    this.loading = true;
    await Promise.all([this.fetchDeveloeprList(), this.fetchPlatformTypes()]);
    this.loading = false;
  },
  async mounted() {
    this.roleOptions = SharingPermissionEnum.allOptions(false);
    if (this.roleOptions.length > 0) {
      this.inviteRole = this.roleOptions[0].id;
    }
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  },
};
</script>

<style scoped>
/**Multi select filter */

/** Filter */
.right-strip {
  display: flex;
}

.filter-container {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  align-items: center;
}

.strip {
  overflow-x: visible !important;
}

.item-card {
  margin-bottom: 5px;
  padding: 15px !important;
  flex: 1 1 calc(100%) !important;
}
/** */
.invite-section {
  display: flex;
  align-items: center;
  gap: 10px;
}

.input-select-container {
  display: flex;
  align-items: center;
  border: 1px solid #d1d5db; /* Hafif gri kenarlık */
  border-radius: 8px;
  overflow: hidden;
  width: 100%;
}

.invite-input {
  flex: 1;
  border: none;
  padding: 10px;
  font-size: 14px;
  outline: none;
}

.role-select {
  margin: 8px 5px;
  cursor: pointer;
  border: none;
}

.invite-btn {
  padding: 10px 20px;
  font-size: 14px;
  background-color: black;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  height: 100%;
}

.invite-btn:hover {
  background-color: rgb(36, 36, 36);
}

.user-list {
  margin-top: 20px;
}

.user-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 0px;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.user-details {
  display: flex;
  flex-direction: column; /* Alt alta dizilim */
  gap: 4px;
}

.user-actions {
  display: flex;
  gap: 10px;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
}

.user-name {
  font-size: 14px;
  font-weight: bold;
}

.user-email {
  font-size: 14px;
}

.current-user {
  font-size: 12px;
  font-style: italic;
  color: gray;
  margin-left: 5px;
}

.user-role select {
  padding: 6px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.user-remove {
  cursor: pointer;
  display: flex;
  align-items: center;
}

.spinner {
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-left-color: #000;
  border-radius: 50%;
  width: 1em;
  height: 1em;
  animation: spin 0.6s linear infinite;
  display: inline-block;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.invite-btn:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}
</style>
